import * as Select from "@radix-ui/react-select";
import { CheckIcon, ChevronDownIcon } from "@shared/ui.icons";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";

import { Typography } from "../Typography/Typography";

import {
  dropdownContainer,
  dropdownIcon,
  dropdownOption,
  dropdownOptionCheckmark,
  dropdownTriggerContainer,
  dropdownViewportContainer
} from "./Dropdown.css";

type DropdownVariant = "default" | "error" | "success" | "disabled";

export type Option<T = string> = {
  label: string;
  value: T;
};

type Options<T = string> = {
  dataTestId: string;
  items: Option<T>[];
  variant?: DropdownVariant;
};

type TriggerProps = {
  ariaLabel: string;
  dataTestId: string;
  options: Options;
  preselectedOption?: Option;
  variant?: DropdownVariant;
  onChange: (value: string) => void;
};

type DropdownProps = {
  ariaLabel: string;
  dataTestId?: string;
  isDisabled?: boolean;
  label?: string;
  onChange: (value: string) => void;
  options: Options;
  preselectedOption?: Option;
  variant?: "default" | "error" | "success";
};

const DropdownOption = forwardRef<
  HTMLDivElement,
  Options & { selectedValue?: string }
>(({ dataTestId, items: options, variant, selectedValue }, forwardedRef) => {
  return (
    <>
      {options.map(({ value, label }) => {
        const isSelected = selectedValue === value;
        return (
          <Select.Item
            className={dropdownOption}
            data-testid={`${dataTestId}-option`}
            ref={forwardedRef}
            key={value}
            value={value}
          >
            <Select.ItemText>
              <Typography
                colour={variant === "error" ? "error-dark" : "primary-dark"}
                variant={isSelected ? "body-md-bold" : "body-md"}
              >
                {label}
              </Typography>
            </Select.ItemText>
            <Select.ItemIndicator>
              <CheckIcon className={dropdownOptionCheckmark} />
            </Select.ItemIndicator>
          </Select.Item>
        );
      })}
    </>
  );
});

const getKeyFromValue = ({ items: options }: Options, value: string) =>
  options.find((option) => option.value === value)?.label ?? "";

const areAllValuesPresent = (obj: Option) =>
  Object.values(obj).every((value) => value);

const DropdownTrigger = ({
  ariaLabel,
  dataTestId,
  onChange,
  options,
  preselectedOption,
  variant = "default"
}: TriggerProps): JSX.Element => {
  const initialOption = useMemo(
    () =>
      preselectedOption && areAllValuesPresent(preselectedOption)
        ? preselectedOption
        : options.items[0],
    [preselectedOption, options]
  );

  const [selectedOption, setSelectedOption] = useState(initialOption);
  const [isFlipped, setIsFlipped] = useState(false);
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let debounceTimer: NodeJS.Timeout | null = null;

    const handleScroll = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (dropdownTriggerRef.current) {
          const { top, bottom } =
            dropdownTriggerRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const componentMiddle = (top + bottom) / 2;

          setIsFlipped(componentMiddle >= viewportHeight / 2);
        }
      }, 100);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, []);

  useEffect(() => {
    if (selectedOption.value === initialOption.value) return;
    onChange(selectedOption.value);
  }, [selectedOption]);

  return (
    <Select.Root
      onValueChange={(value: string) =>
        setSelectedOption({
          label: getKeyFromValue(options, value),
          value
        })
      }
      value={selectedOption.value}
    >
      <Select.Trigger
        className={dropdownTriggerContainer({ state: variant ?? "default" })}
        data-testid="dropdown-trigger"
        aria-label={ariaLabel}
        ref={dropdownTriggerRef}
        disabled={variant === "disabled"}
      >
        <Select.Value placeholder={selectedOption.label} />
        <Select.Icon>
          <ChevronDownIcon className={dropdownIcon} />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          className={dropdownViewportContainer}
          position="popper"
          side={isFlipped ? "top" : "bottom"}
          sideOffset={4}
        >
          <Select.Viewport>
            <DropdownOption
              dataTestId={dataTestId}
              items={options.items}
              variant={options.variant || variant}
              selectedValue={selectedOption.value}
            />
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
};

/**
 * Dropdown component using Radix UI Select primitives.
 *
 * @remarks
 * This component renders a customizable dropdown menu with support for variants, preselected options, and disabled state.
 * It uses Radix UI's Select for accessibility and composability, and applies custom styling via CSS modules.
 *
 * @example
 * ```tsx
 * <Dropdown
 *   ariaLabel="Choose an option"
 *   label="Options"
 *   options={[
 *     { label: "Option 1", value: "1" },
 *     { label: "Option 2", value: "2", variant: "error" }
 *   ]}
 *   onChange={(value) => console.log(value)}
 * />
 * ```
 *
 * @param ariaLabel - Accessible label for the dropdown trigger.
 * @param dataTestId - Optional test ID for the dropdown container.
 * @param isDisabled - If true, disables the dropdown.
 * @param label - Optional label displayed above the dropdown.
 * @param onChange - Callback fired when the selected value changes.
 * @param options - Array of selectable options.
 * @param preselectedOption - Option to be preselected on mount.
 * @param variant - Visual variant of the dropdown ("default", "error", "success").
 *
 * @returns JSX.Element
 */
export const Dropdown = ({
  ariaLabel,
  dataTestId = "dropdown",
  isDisabled = false,
  label,
  onChange,
  options,
  preselectedOption,
  variant = "default"
}: DropdownProps): JSX.Element => {
  const styleVariant: DropdownVariant = isDisabled ? "disabled" : variant;

  return (
    <div className={dropdownContainer} data-testid={dataTestId}>
      {label && <Typography variant="subheader-6">{label}</Typography>}
      <DropdownTrigger
        ariaLabel={ariaLabel}
        dataTestId={dataTestId}
        options={options}
        preselectedOption={preselectedOption}
        variant={styleVariant}
        onChange={onChange}
      />
    </div>
  );
};
